import React, { useState, useRef, useEffect, useContext } from "react";
import { IonCol, IonInput, IonItem, IonLabel, IonRow } from "@ionic/react";
import { isWindows } from "../../../utilities/platform/Platform";
import { isPlatform } from "@ionic/core";
import { Label } from "reactstrap";
import { Address, Barn, Rider } from "../../../models";
import { formatSingleRider, formattedOption, getRidersCreatedByUserOrInCurrentBarn } from "../../../utilities/rider/SelectOrCreateRiderUtils";
import { PersonContext } from "../../../context/PersonContext";
import { CreateAddressInput, CreateRiderInput, UpdateAddressInput, UpdateRiderInput } from "../../../API";
import { createRider, updateRider } from "../../../utilities/rider/Rider";
import { formatShortAddress } from "../../../utilities/address/FormatAddress";
import { createAddress, updateAddress } from "../../../utilities/address/Address";
import SelectOrCreateRider from "../../Rider/SelectOrCreateRider";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import SelectState from "../../Address/SelectState";
import RiderSelectStatus from "../../Rider/RiderSelectStatus";
import BirthdateInput from "../../DatePicker/BirthdateInput";
import { FormFieldsInlineSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import MembershipUI from "./MembershipUI";
import moment from "moment";

interface _Props {
    requiredFields?: string[];
    barn?: Barn | null;
    selectedRider?: Rider | null;
    onSelectRider?: (rider?: Rider, areAllRequiredFieldsComplete?: Boolean) => void;
    onSavingStateChange?: (isSaving: boolean) => void;
    onFieldChange?: (rider?: Rider, areAllRequiredFieldsComplete?: Boolean) => void;
    excludedRiderId?: string | null | undefined;
    disabledRiderLabel?: string;
    sectionId?: "rider" | "rider2";
}

interface RiderFormState {
    name: string;
    riderDOB: string;
    riderStatus: boolean | null;
    city: string;
    state: string;
}

interface RiderSectionParentHooks {
    onRegisterSave?: (fn: () => Promise<void> | void) => void;
}

const SelectRiderSection: React.FC<_Props & RiderSectionParentHooks> = ({
    onRegisterSave,
    requiredFields,
    barn,
    selectedRider,
    onSelectRider,
    onSavingStateChange,
    onFieldChange,
    excludedRiderId,
    disabledRiderLabel,
    sectionId = "rider",
}) => {
    const user = useContext(PersonContext);
    const [isLoading, setIsLoading] = useState(false);

    // Add ref for click outside detection
    const riderComponentRef = useRef<HTMLIonRowElement>(null);

    // Add ref to track the current rider state for the click outside handler
    const selectedRiderRef = useRef<Rider | null | undefined>(selectedRider);

    // Ref to track the latest formState for use in save function (avoids stale closure issues)
    const formStateRef = useRef<RiderFormState>({
        name: "",
        riderDOB: "",
        riderStatus: null,
        city: "",
        state: "",
    });
    // Track the last rider name we tried to save to prevent duplicate saves
    const lastSavedRiderNameRef = useRef<string>("");

    // selectedRiderState triggers re-renders and allows us to bind data to the UI
    const [selectedRiderState, setSelectedRiderState] = useState<Rider | null | undefined>();
    const [formState, setFormState] = useState<RiderFormState>({
        name: "",
        riderDOB: "",
        riderStatus: null,
        city: "",
        state: "",
    });

    const [userRiderOptions, setUserRiderOptions] = useState<formattedOption[] | null | undefined>();
    const [barnRiderOptions, setBarnRiderOptions] = useState<formattedOption[] | null | undefined>();

    // Load inital User and Barn options to populate the dropdown
    useEffect(() => {
        const getData = async () => {
            setIsLoading(true);
            const result = await getRidersCreatedByUserOrInCurrentBarn(user, barn, userRiderOptions || undefined);

            const createdByOptions = result.createdBy;
            const barnOptions = result.barn;

            setUserRiderOptions(createdByOptions);
            setBarnRiderOptions(barnOptions);
            setIsLoading(false);
        };

        getData();
    }, [barn]);

    // Check for a pre-saved Rider from the parent component (ex: already completed Entry)
    // Compare by ID and data fields to detect changes even if object reference is the same
    useEffect(() => {
        if (selectedRider) {
            // Get the data from the new rider prop
            const newRiderCity = getCityFromRider(selectedRider);
            const newRiderState = getStateFromRider(selectedRider);
            const newFormState = {
                name: selectedRider.name || "",
                riderDOB: selectedRider.birthdate || "",
                riderStatus: !!selectedRider.isProfessional,
                city: newRiderCity || "",
                state: newRiderState || "",
            };

            // Check if rider ID changed (different rider selected)
            const riderIdChanged = selectedRiderState?.id !== selectedRider.id;

            // Also check if data fields differ (same rider but updated data)
            const dataChanged =
                !selectedRiderState || // No previous state
                formState.name !== newFormState.name ||
                formState.riderDOB !== newFormState.riderDOB ||
                formState.riderStatus !== newFormState.riderStatus ||
                formState.city !== newFormState.city ||
                formState.state !== newFormState.state;

            if (riderIdChanged || dataChanged) {
                // Update state with the new rider data
                // Always update when rider ID changes, regardless of form state comparison
                setSelectedRiderState(selectedRider);
                setFormState(newFormState);
                formStateRef.current = newFormState; // Update ref immediately
                // Evaluate if this pre-selected Rider has all required fields in order to be considered "Complete"
                const isSectionValid = checkIfSectionIsValid(newFormState);
                if (onFieldChange) onFieldChange(selectedRider, isSectionValid);
            }
        } else {
            // If the parent component does NOT pass in a pre-selected Rider, then clear all values
            if (selectedRiderState !== undefined) {
                setSelectedRiderState(undefined);
                const currentFormState = {
                    name: "",
                    riderDOB: "",
                    riderStatus: false,
                    city: "",
                    state: "",
                };
                setFormState(currentFormState);
                formStateRef.current = currentFormState; // Update ref immediately
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedRider?.id,
        selectedRider?.name,
        selectedRider?.birthdate,
        selectedRider?.isProfessional,
        selectedRider?.address?.city,
        selectedRider?.address?.provState,
        selectedRider?.location,
    ]);

    // Keep selectedRiderRef updated
    useEffect(() => {
        selectedRiderRef.current = selectedRiderState;
    }, [selectedRiderState]);

    // Keep formStateRef updated so save function can access latest formState
    useEffect(() => {
        formStateRef.current = formState;
    }, [formState]);

    // Trigger immediate save when a new rider name is set (no existing rider selected)
    // This ensures riders created via VerifyRiderModal are saved immediately
    useEffect(() => {
        // Only trigger if:
        // 1. There's a rider name in formState
        // 2. No rider is currently selected
        // 3. We're not already saving
        // 4. We haven't already saved this rider name
        // 5. At least the name field is provided (for new creation, we only need name)
        if (formState.name && !selectedRiderState && !isSavingRef.current && formState.name !== lastSavedRiderNameRef.current) {
            // For new entity creation, only require the name field
            // Other fields can be added later
            const hasRequiredName = !checkIfFieldIsRequired("name") || formState.name !== "";
            if (hasRequiredName) {
                lastSavedRiderNameRef.current = formState.name; // Mark as attempting to save
                // Use a small delay to ensure all state updates are processed
                const timeoutId = setTimeout(() => {
                    handleSaveRiderData();
                }, 150);
                // Cleanup timeout if component unmounts or dependencies change
                return () => clearTimeout(timeoutId);
            }
        }
        // Reset the saved name ref when a rider is selected (so we can save new names again)
        if (selectedRiderState) {
            lastSavedRiderNameRef.current = "";
        }
    }, [formState.name, selectedRiderState]);

    // Add ref to track if save is in progress to prevent duplicate calls
    const isSavingRef = useRef(false);

    // Register save fn with parent
    useEffect(() => {
        if (!onRegisterSave) return;
        const fn = () => {
            // Use formStateRef to get the latest formState value (avoids stale closure)
            const currentFormState = formStateRef.current;
            if (selectedRiderRef.current) {
                const currentRider = selectedRiderRef.current;
                const riderCity = getCityFromRider(currentRider);
                const riderState = getStateFromRider(currentRider);
                const hasChanges =
                    (currentFormState.name && currentFormState.name !== currentRider.name) ||
                    currentFormState.city !== riderCity ||
                    currentFormState.state !== riderState ||
                    currentFormState.riderDOB !== currentRider.birthdate ||
                    currentFormState.riderStatus !== currentRider.isProfessional;
                if (hasChanges) {
                    return handleSaveRiderData(currentRider);
                }
            } else if (currentFormState.name) {
                return handleSaveRiderData();
            }
        };
        onRegisterSave(fn);
    }, [onRegisterSave, formState, selectedRiderRef.current]);

    const handleSelectedRider = async (rider?: Rider, riderName?: string) => {
        setIsLoading(true);

        let currentRider: Rider | null | undefined = rider;

        const riderCity = currentRider ? getCityFromRider(currentRider) : "";
        const riderState = currentRider ? getStateFromRider(currentRider) : "";

        const currentFormState = {
            name: currentRider?.name || riderName || "",
            riderDOB: currentRider?.birthdate || "",
            riderStatus: currentRider?.isProfessional || false,
            city: riderCity || "",
            state: riderState || "",
        };

        // Don't update selectedRiderState or formState here - let useEffect handle it when prop changes
        // This ensures useEffect can properly detect the change and update all fields correctly
        // Just call the parent callback to trigger the prop update
        // const isSectionValid = checkIfSectionIsValid(currentFormState);
        // if (onSelectRider) onSelectRider(currentRider, isSectionValid);


        setSelectedRiderState(currentRider);
        setFormState(currentFormState);
        formStateRef.current = currentFormState;
        const isSectionValid = checkIfSectionIsValid(currentFormState);
        if (onSelectRider) {
            onSelectRider(currentRider || undefined, isSectionValid);
        }

        setIsLoading(false);
    };

    const handleFieldChange = (updatedFormState: RiderFormState) => {
        if (onFieldChange) {
            const isSectionValid = checkIfSectionIsValid(updatedFormState);
            onFieldChange(selectedRiderState || undefined, isSectionValid);
        }
    };

    const handleRiderStatusChange = (isProfessional?: (boolean | null)) => {
        const updatedFormState: RiderFormState = {
            name: formState.name,
            riderDOB: formState.riderDOB,
            riderStatus: isProfessional === true ? true : (isProfessional === false ? false : null),
            city: formState.city,
            state: formState.state,
        };
        const riderStatus: boolean | null = (isProfessional === true ? true : (isProfessional === false ? false : null));
        setFormState((prev) => ({
            ...prev,
            riderStatus: riderStatus,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleDateChange = (dob: string) => {
        const updatedFormState: RiderFormState = {
            name: formState.name,
            riderDOB: dob,
            riderStatus: formState.riderStatus,
            city: formState.city,
            state: formState.state,
        };
        setFormState((prev) => ({
            ...prev,
            riderDOB: dob,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleCityChange = (e: any) => {
        const newCity = e.target.value;
        const updatedFormState: RiderFormState = {
            name: formState.name,
            riderDOB: formState.riderDOB,
            riderStatus: formState.riderStatus,
            city: newCity,
            state: formState.state,
        };
        setFormState((prev) => ({
            ...prev,
            city: e.target.value,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleStateSelection = (selectedState: string) => {
        const updatedFormState: RiderFormState = {
            name: formState.name,
            riderDOB: formState.riderDOB,
            riderStatus: formState.riderStatus,
            city: formState.city,
            state: selectedState === "-" ? "" : selectedState,
        };
        setFormState((prev) => ({
            ...prev,
            state: selectedState === "-" ? "" : selectedState,
        }));
        handleFieldChange(updatedFormState);
    };

    const getCityFromRider = (o: Rider) => {
        if (o.address && o.address.city) return o.address.city;
        if (o.location) {
            const riderAddressArray = o.location.split(",");
            return riderAddressArray[0] && riderAddressArray[1] ? riderAddressArray[0] : "";
        }
        return "";
    };

    const getStateFromRider = (o: Rider) => {
        if (o.address && o.address.provState) return o.address.provState;
        if (o.location) {
            const riderAddressArray = o.location.split(",");
            return riderAddressArray[0] && riderAddressArray[1] ? riderAddressArray[1] : "";
        }
        return "";
    };

    const checkIfFieldIsRequired = (fieldName: string) => {
        if (requiredFields && requiredFields.length > 0) {
            const index = requiredFields.findIndex((field) => field.toLocaleLowerCase() === fieldName.toLocaleLowerCase());
            if (index > -1) return true;
        }
        return false;
    };

    const checkIfSectionIsValid = (currentFormState: RiderFormState) => {
        if (checkIfFieldIsRequired("name") && currentFormState.name === "") return false;
        if (checkIfFieldIsRequired("dob") && currentFormState.riderDOB === "") return false;
        if (checkIfFieldIsRequired("status") && currentFormState.riderStatus === null) return false;
        if (checkIfFieldIsRequired("city") && currentFormState.city === "") return false;
        if (checkIfFieldIsRequired("state") && currentFormState.state === "") return false;
        return true;
    };

    const handleSaveRiderData = async (riderNeedingUpdate?: Rider | null | undefined) => {
        // Use formStateRef to get the latest formState value
        const currentFormState = formStateRef.current;
        // If this function is called before any rider was selected or a name was selected, don't perform any actions.
        if (!riderNeedingUpdate && !currentFormState.name) return;

        // For new entity creation, only require the name field
        // For updates, require all fields to be valid
        if (riderNeedingUpdate) {
            // Updating existing rider - require all fields
            const isSectionValid = checkIfSectionIsValid(currentFormState);
            if (!isSectionValid) {
                return;
            }
        } else {
            // Creating new rider - only require name
            const hasRequiredName = !checkIfFieldIsRequired("name") || currentFormState.name !== "";
            if (!hasRequiredName) {
                return;
            }
        }

        // Prevent concurrent saves
        if (isSavingRef.current) {
            return;
        }
        isSavingRef.current = true;
        if (onSavingStateChange) onSavingStateChange(true);

        // Track the updatedRider
        let updatedRider: Rider | null | undefined = riderNeedingUpdate;

        // Track the current value of Address and Location for this rider
        let updatedRiderAddress: Address | null | undefined = updatedRider?.address;
        if (updatedRider?.addressId) {
            const updateInput: UpdateAddressInput = {
                id: updatedRider.addressId,
                city: currentFormState.city || "",
                provState: currentFormState.state || "",
            };
            const updateResult = await updateAddress(updateInput);
            if (updateResult.isSuccess) {
                const updatedAddressResult: Address = updateResult.result;
                updatedRiderAddress = updatedAddressResult;
            }
        } else {
            const createInput: CreateAddressInput = {
                type: "mailing",
                city: currentFormState.city || "",
                provState: currentFormState.state || "",
                notes: "Created during Online Entry process",
            };
            const createResult = await createAddress(createInput);
            if (createResult.isSuccess) {
                const createdAddressResult: Address = createResult.result;
                updatedRiderAddress = createdAddressResult;
            }
        }
        const currentRiderLocationValue = updatedRiderAddress ? formatShortAddress(updatedRiderAddress) : "";

        // First, check if this rider already exists.
        if (riderNeedingUpdate) {
            // Next, check what actually needs to be update in order to avoid an update if the values for the rider were not changed.
            let shouldUpdateRiderInDB = false;
            let updateInput: UpdateRiderInput = {
                id: riderNeedingUpdate.id,
                addressId: updatedRiderAddress?.id || null,
            };
            if (currentFormState.name && currentFormState.name !== riderNeedingUpdate.name) {
                shouldUpdateRiderInDB = true;
                updateInput["name"] = currentFormState.name;
            }
            if (currentRiderLocationValue !== riderNeedingUpdate.location) {
                shouldUpdateRiderInDB = true;
                updateInput["location"] = currentRiderLocationValue;
            }
            if (updatedRiderAddress && updatedRiderAddress?.id !== riderNeedingUpdate.addressId) {
                shouldUpdateRiderInDB = true;
                updateInput["addressId"] = updatedRiderAddress?.id;
            }
            if (currentFormState.riderStatus !== riderNeedingUpdate.isProfessional) {
                shouldUpdateRiderInDB = true;
                updateInput["isProfessional"] = currentFormState.riderStatus;
            }
            if (currentFormState.riderDOB !== riderNeedingUpdate.birthdate) {
                shouldUpdateRiderInDB = true;
                updateInput["birthdate"] = currentFormState.riderDOB !== "" ? currentFormState.riderDOB : null;
            }
            if (shouldUpdateRiderInDB) {
                // Run the following command to update the rider in the DB
                const updateResult = await updateRider(updateInput);
                if (updateResult.isSuccess) {
                    updatedRider = updateResult.result as Rider;
                    const formattedOption = await formatSingleRider(updatedRider);

                    // Find the group that this rider is in
                    const userGroupIndex = userRiderOptions?.findIndex((h) => h.value === updatedRider?.id);
                    const barnGroupIndex = barnRiderOptions?.findIndex((h) => h.value === updatedRider?.id);

                    // Update the appropriate group
                    if (barnGroupIndex !== undefined && barnGroupIndex > -1 && barnRiderOptions) {
                        const updatedBarnGroup: formattedOption[] = [
                            ...barnRiderOptions?.slice(0, barnGroupIndex),
                            formattedOption,
                            ...barnRiderOptions?.slice(barnGroupIndex + 1),
                        ];
                        setBarnRiderOptions(updatedBarnGroup);
                    } else if (userGroupIndex !== undefined && userGroupIndex > -1 && userRiderOptions) {
                        const updatedUserGroup: formattedOption[] = [
                            ...userRiderOptions?.slice(0, userGroupIndex),
                            formattedOption,
                            ...userRiderOptions?.slice(userGroupIndex + 1),
                        ];
                        setUserRiderOptions(updatedUserGroup);
                    } else {
                        // Could not find the rider in either list
                    }
                } else {
                    // TODO: show error
                }
            }
        }

        // OTW, check if this is a new rider that needs to be created.
        else if (currentFormState.name) {
            const createInput: CreateRiderInput = {
                name: currentFormState.name,
                location: currentRiderLocationValue || "",
                addressId: updatedRiderAddress?.id || "",
                barnId: barn?.id || null,
                barnName: barn?.name || "",
                birthdate: currentFormState.riderDOB || null,
                isProfessional: currentFormState.riderStatus || false,
                createdBy: user.id,
                createdOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
                updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
            };
            const createResult = await createRider(createInput);
            if (createResult.isSuccess) {
                const newRider: Rider = createResult.result;

                // Set the selectedRiderRef to now point to the newly created rider.
                // This prevents a duplicated rider from being created, as well as allows the user to update the rider's properties.
                selectedRiderRef.current = newRider;
                updatedRider = newRider;

                // Format the rider for the dropdown
                const formattedOption = await formatSingleRider(newRider, user);

                // Determine which list to add the rider to based on barn
                const riderBelongsToCurrentBarn = barn && (newRider.barnId === barn.id || createInput.barnId === barn.id);

                // Update state in a way that ensures React detects the change
                if (riderBelongsToCurrentBarn) {
                    // Rider belongs to this barn - add to barnRiderOptions
                    setBarnRiderOptions((prevBarnRiders) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevBarnRiders?.some((h) => h.value === newRider.id);
                        if (isAlreadyInList) {
                            return prevBarnRiders;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevBarnRiders || [];
                        const updatedBarnRiders: formattedOption[] = [...baseArray, formattedOption];
                        // Sort alphabetically by rider name
                        const sorted = updatedBarnRiders.sort((a, b) => a.riderName.trim().localeCompare(b.riderName.trim()));
                        // Force a new array reference even after sorting
                        return [...sorted];
                    });

                    // Remove from userRiderOptions if it exists there (to avoid duplicates)
                    setUserRiderOptions((prevUserRiders) => {
                        if (prevUserRiders && prevUserRiders.length > 0) {
                            return prevUserRiders.filter((h) => h.value !== newRider.id);
                        }
                        return prevUserRiders;
                    });
                } else {
                    // Rider doesn't belong to a barn or belongs to a different barn - add to userRiderOptions
                    setUserRiderOptions((prevUserRiders) => {
                        // Check if already in list to prevent duplicates
                        const isAlreadyInList = prevUserRiders?.some((h) => h.value === newRider.id);
                        if (isAlreadyInList) {
                            return prevUserRiders;
                        }
                        // Always create a new array to ensure React detects the change
                        const baseArray = prevUserRiders || [];
                        const updatedUserRiders: formattedOption[] = [...baseArray, formattedOption];
                        return updatedUserRiders.sort((a, b) => a.riderName.trim().localeCompare(b.riderName.trim()));
                    });

                    // Remove from barnRiderOptions if it exists there
                    setBarnRiderOptions((prevBarnRiders) => {
                        if (prevBarnRiders && prevBarnRiders.length > 0) {
                            return prevBarnRiders.filter((h) => h.value !== newRider.id);
                        }
                        return prevBarnRiders;
                    });
                }

                // Update selectedRiderState after options are updated to ensure dropdown shows the rider
                setSelectedRiderState(newRider);

                // Also update formState to match the newly created rider
                const riderCity = getCityFromRider(newRider);
                const riderState = getStateFromRider(newRider);
                const updatedFormState: RiderFormState = {
                    name: newRider.name || currentFormState.name,
                    riderDOB: newRider.birthdate || currentFormState.riderDOB,
                    riderStatus: newRider.isProfessional !== undefined ? newRider.isProfessional : currentFormState.riderStatus,
                    city: riderCity || currentFormState.city,
                    state: riderState || currentFormState.state,
                };
                setFormState(updatedFormState);
                formStateRef.current = updatedFormState;

                // Notify parent component
                const isSectionValid = checkIfSectionIsValid(updatedFormState);
                if (onSelectRider) {
                    onSelectRider(newRider, isSectionValid);
                }
                if (onFieldChange) {
                    onFieldChange(newRider, isSectionValid);
                }

                // Reset the saved name ref since we successfully created the rider
                lastSavedRiderNameRef.current = "";
            } else {
                // Reset the saved name ref on failure so user can retry
                lastSavedRiderNameRef.current = "";
            }
        }

        // For updates (not new riders), we need to update selectedRiderState
        if (updatedRider && riderNeedingUpdate) {
            setSelectedRiderState(updatedRider);
        }
        isSavingRef.current = false;
        if (onSavingStateChange) onSavingStateChange(false);
    };

    return (
        <>
            <div data-section={sectionId}>
                <IonRow ref={riderComponentRef}>
                    <IonCol sizeXs="12" sizeMd={formState.name ? "6" : "12"} className={`${isWindows() || isPlatform("desktop") ? "" : "pb-0"}`}>
                        <Label>
                            Name
                            {checkIfFieldIsRequired("name") && <RequiredInputIndicator />}
                        </Label>
                        <SelectOrCreateRider
                            key={`rider-select-${barn?.id || "no-barn"}-${barnRiderOptions?.length || 0}-${userRiderOptions?.length || 0}`}
                            userRiderOptions={userRiderOptions}
                            barnRiderOptions={barnRiderOptions}
                            selectedBarn={barn}
                            selectedValue={selectedRiderState?.id}
                            onSelect={(rider?: Rider, riderName?: string) => {
                                handleSelectedRider(rider, riderName);
                            }}
                            disabledRiderId={excludedRiderId}
                            disabledRiderLabel={disabledRiderLabel}
                        />
                    </IonCol>

                    {formState.name && (
                        // Layout for when name is provided
                        <>
                            <IonCol sizeXs="12" sizeMd="6">
                                <RiderSelectStatus
                                    rider={selectedRiderState}
                                    isRequired={checkIfFieldIsRequired("status")}
                                    onChange={(isProfessional?: (boolean | null)) => handleRiderStatusChange(isProfessional)}
                                />
                            </IonCol>
                            <IonCol sizeXs="12" sizeMd="6" className="py-0">
                                <BirthdateInput label="Birth Date" value={formState.riderDOB} isRequired={checkIfFieldIsRequired("dob")} onChange={handleDateChange} />
                            </IonCol>
                            <IonCol sizeXs="12" sizeMd="4.5">
                                <IonItem color="white">
                                    <IonLabel position="stacked">
                                        City
                                        {checkIfFieldIsRequired("city") && <RequiredInputIndicator />}
                                    </IonLabel>
                                    <IonInput type="text" value={formState.city} aria-required={true} onIonChange={handleCityChange} />
                                </IonItem>
                            </IonCol>
                            <IonCol sizeXs="12" sizeMd="1.5">
                                <SelectState selectedValue={formState.state} isRequired={checkIfFieldIsRequired("state")} onInputChange={handleStateSelection} className="mb-0" />
                            </IonCol>
                            <MembershipUI
                                entityType="rider"
                                entityId={selectedRiderState?.id}
                                disabled={!formState.name}
                                ensureEntityExists={async () => {
                                    if (!selectedRiderState?.id && formState.name && checkIfSectionIsValid(formState)) {
                                        await handleSaveRiderData(selectedRiderRef.current);
                                    }
                                    return selectedRiderRef.current?.id;
                                }}
                            />
                        </>
                    )}

                    {isLoading && <FormFieldsInlineSkeleton />}
                </IonRow>
            </div>
        </>
    );
};

export default SelectRiderSection;
